import type { ComponentPropsOptions } from "vue";

import omit from "lodash/omit";
import { computed, defineComponent, ref } from "vue";
import Spinner from "@/components/Spinner";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { buttonProps } from "../button/Button";
import { buttonVariants } from "../button/variants";
import Icon from "../icon/Icon";
import Tooltip from "../tooltip/Tooltip";

export const iconButtonProps = {
  ...omit(buttonProps, ["label", "block", "onClick"]),
  label: p.$string(),
  icon: p.$string(),
  onClick: p.func<(e?: MouseEvent) => any>(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  inheritAttrs: false,
  name: "IconButton",
  props: iconButtonProps,
  setup(props, { attrs }) {
    const sizeClass = computed(() => {
      switch (props.size) {
        case "xs":
          return "h-6 w-6 p-1";
        case "sm":
          return "h-7 w-7 p-1.5";
        case "md":
          return "h-8 w-8 p-2";
        case "lg":
          return "h-9 w-9 p-2";
        default:
          return "h-12 w-12 p-1";
      }
    });

    const internalLoading = ref(false);
    const loading = computed(() => props.loading || internalLoading.value);

    const resolvedVariant = computed(() => {
      if (props.variant === "flex" || props.variant === "text" || props.variant === "link") {
        return "ghost";
      }
      return props.variant;
    });

    async function onClick(e: MouseEvent) {
      if (loading.value) return;

      const result = props.onClick?.(e);

      if (result instanceof Promise) {
        internalLoading.value = true;
        await result.catch(console.error).finally(() => (internalLoading.value = false));
      }
    }

    return () => (
      <Tooltip text={props.label}>
        <div>
          <button
            aria-label={props.label}
            disabled={props.disabled}
            type={props.type ?? "button"}
            data-accent={props.color}
            onClick={onClick}
            class={cn(
              buttonVariants({
                radius: props.radius,
                variant: resolvedVariant.value,
              }),
              sizeClass.value,
              attrs.class ?? ""
            )}
          >
            {loading.value ? <Spinner /> : <Icon icon={props.icon} class="h-full w-full" />}
          </button>
        </div>
      </Tooltip>
    );
  },
});
